export type RangePreset = '1h' | '6h' | '24h' | '7d';

const PRESETS: readonly { key: RangePreset; label: string }[] = [
  { key: '1h', label: 'Last 1h' },
  { key: '6h', label: 'Last 6h' },
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: 'Last 7d' },
];

export interface DateRangePickerProps {
  readonly value: RangePreset;
  readonly onChange: (preset: RangePreset) => void;
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps): JSX.Element {
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Time range">
      {PRESETS.map((p) => {
        const active = p.key === value;
        return (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(p.key)}
            className={[
              'px-2.5 py-1 text-xs font-mono rounded-md border transition-colors',
              active
                ? 'border-accent text-accent bg-card'
                : 'border-border text-text-dim hover:text-text hover:border-border-2',
            ].join(' ')}
          >
            {p.label}
          </button>
        );
      })}
      <button
        type="button"
        disabled
        title="Custom ranges land in v1.1"
        className="px-2.5 py-1 text-xs font-mono rounded-md border border-border text-muted disabled:cursor-not-allowed"
      >
        Custom
      </button>
    </div>
  );
}

export function rangeToBounds(preset: RangePreset): { from: string; to: string; limit: number } {
  const now = new Date();
  const map: Record<RangePreset, { hours: number; limit: number }> = {
    '1h': { hours: 1, limit: 240 },
    '6h': { hours: 6, limit: 360 },
    '24h': { hours: 24, limit: 480 },
    '7d': { hours: 24 * 7, limit: 1000 },
  };
  const { hours, limit } = map[preset];
  const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
  return {
    from: from.toISOString(),
    to: now.toISOString(),
    limit,
  };
}
